import React, { useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Container, SpaceBetween } from '@amzn/awsui-components-react';
import ComponentCheck from '../../components/component_install/componentCheck';
import ComponentInstall from '../../components/component_install/componentInstall';
import InstallationProgress from '../../components/component_install/installationProgress';
import EmeraldHealthChecker from '../../components/component_install/emeraldHealthChecker';
import InstallationSuccess from '../../components/component_install/installationSuccess';
import SoftwarePlatformCheck from '../../components/component_install/softwarePlatformCheck';
import updateDeviceComponentStatusHandler, {
    UpdateDeviceComponentStatusRequest,
} from '../../services/updateDeviceComponentStatusHandler';
import { EventType } from '../../services/eventService';
import { logComponentInstallationEvent } from '../../utils/logComponentInstallationEvent';
import { useDispatch } from 'react-redux';
import { addInstall } from '../../slice/installationDataSlice';
import { OnboardingStatus, fromValue, isInstallComplete } from '../../constants/onboardingStatus';
import { delayedApiCall } from '../../utils/delayedApiCall';
import { ComponentType } from '../../components/component_install/componentType';

export enum ComponentOnboardingState {
    COMPONENT_INSTALL_PRECHECK = 1,
    SOFTWARE_PLATFORM_CHECK = 2,
    COMPONENT_INSTALL = 3,
    IN_PROGRESS = 4,
    HEALTH_CHECK = 5,
    FINISHED_SUCCESS = 6,
    FINISHED_FAILURE = 7,
}

const ComponentOnboardingFormPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const vin = params.get('vin') || '';
    const deviceId = params.get('deviceId') || '';

    const dispatch = useDispatch();

    const [deviceInfo, setDeviceInfo] = useState<any>(null);
    const [componentOnboardingState, setComponentOnboardingState] = useState<ComponentOnboardingState>(
        ComponentOnboardingState.COMPONENT_INSTALL_PRECHECK,
    );
    const [startTime, setStartTime] = useState(0);
    const [currentComponentType, setCurrentComponentType] = useState<ComponentType | null>(null);
    const [currentSerialNumber, setCurrentSerialNumber] = useState<string>('');
    const [currentNotes, setCurrentNotes] = useState<string>('');
    const [existingComponentInfo, setExistingComponentInfo] = useState<any>(null);
    const [emeraldInformation, setEmeraldInformation] = useState<any>(null);
    const [isSubmittingInstall, setIsSubmittingInstall] = useState<boolean>(false);
    const [showFailedInstallationWarning, setShowFailedInstallationWarning] = useState<boolean>(false);

    const markDone = useCallback(
        (
            success: boolean,
            componentType: ComponentType,
            serialNumber: string,
            notes: string,
            data?: any,
            reason?: string,
        ) => {
            setComponentOnboardingState(
                success ? ComponentOnboardingState.FINISHED_SUCCESS : ComponentOnboardingState.FINISHED_FAILURE,
            );

            dispatch(
                addInstall({
                    vin: vin,
                    deviceSerial: deviceId,
                    vinCheck: data && vin === data['vin'],
                    installationSuccessful: success,
                    time: Date.now() / 1000,
                    componentType: componentType!.value,
                    notes: notes,
                    installationType: 'component',
                }),
            );

            logComponentInstallationEvent({
                deviceId,
                vin,
                componentType: componentType!.value,
                componentId: serialNumber,
                eventType: success ? EventType.SUCCESS : EventType.FAILURE,
                startTime,
                notes,
                ...(success ? {} : { error: reason }),
            });
        },
        [vin, deviceId, dispatch, startTime],
    );

    // Handle failed installation warning
    useEffect(() => {
        if (showFailedInstallationWarning) {
            const timer = setTimeout(() => {
                setShowFailedInstallationWarning(false);
                setComponentOnboardingState(ComponentOnboardingState.SOFTWARE_PLATFORM_CHECK);
            }, 2000); // 2 seconds delay

            return () => clearTimeout(timer);
        }
    }, [showFailedInstallationWarning]);

    const handleCancel = useCallback(() => {
        navigate('/');
    }, []);

    const handlePreCheckComplete = useCallback(
        (hasExisting: boolean, componentType?: any, existingComponentInfo?: any) => {
            setCurrentComponentType(componentType);
            setExistingComponentInfo(existingComponentInfo);

            if (hasExisting) {
                setCurrentSerialNumber(existingComponentInfo?.componentId ?? 'Unknown');
                setCurrentNotes(existingComponentInfo?.errorMessage ?? '');

                const convertedStatus = fromValue(existingComponentInfo?.status);
                if (isInstallComplete(convertedStatus)) {
                    // If installation is completed, show the existing component information
                    setComponentOnboardingState(ComponentOnboardingState.FINISHED_SUCCESS);
                    return;
                } else if (convertedStatus === OnboardingStatus.FAILED) {
                    // If installation failed, show warning first, then go to health check after delay
                    setShowFailedInstallationWarning(true);
                    return;
                }
            }

            // Always proceed to software platform check for new components or existing components that are not completed/failed
            setComponentOnboardingState(ComponentOnboardingState.SOFTWARE_PLATFORM_CHECK);
        },
        [],
    );

    const handleSoftwarePlatformCheckSuccess = useCallback(
        (deviceInfo: any) => {
            setDeviceInfo(deviceInfo);

            // If there's an existing component that's in progress, go to IN_PROGRESS state
            // Otherwise, go to COMPONENT_INSTALL for new installations
            if (existingComponentInfo) {
                setComponentOnboardingState(ComponentOnboardingState.IN_PROGRESS);
            } else {
                setComponentOnboardingState(ComponentOnboardingState.COMPONENT_INSTALL);
            }
        },
        [existingComponentInfo],
    );

    const handleInstallComponentFromNew = useCallback(
        (serialNumber: string) => {
            setIsSubmittingInstall(true);
            setCurrentSerialNumber(serialNumber);
            setCurrentNotes('');

            const time = Date.now();
            setStartTime(time);

            const updateRequest: UpdateDeviceComponentStatusRequest = {
                deviceId: deviceId,
                componentId: serialNumber,
                vin: vin,
                componentType: currentComponentType!.value,
                status: OnboardingStatus.JOB_CREATED,
                notes: 'Component installation job created',
            };

            delayedApiCall((callback) => {
                updateDeviceComponentStatusHandler(updateRequest, callback);
            })
                .then(({ success, result }) => {
                    setIsSubmittingInstall(false);

                    if (success) {
                        // API call successful, proceed to installation progress
                        setComponentOnboardingState(ComponentOnboardingState.IN_PROGRESS);

                        logComponentInstallationEvent({
                            deviceId,
                            vin,
                            componentType: currentComponentType!.value,
                            componentId: serialNumber,
                            eventType: EventType.START,
                            startTime: time,
                            apiResponse: result,
                        });
                    } else {
                        // API call failed, log the error
                        logComponentInstallationEvent({
                            deviceId,
                            vin,
                            componentType: currentComponentType!.value,
                            componentId: serialNumber,
                            eventType: EventType.FAILURE,
                            startTime: time,
                            error: 'Failed to call update_device_component_status API',
                            apiError: result,
                        });
                    }
                })
                .catch(() => {
                    setIsSubmittingInstall(false);
                    // Log the error
                    logComponentInstallationEvent({
                        deviceId,
                        vin,
                        componentType: currentComponentType!.value,
                        componentId: serialNumber,
                        eventType: EventType.FAILURE,
                        startTime: time,
                        error: 'An unexpected error occurred',
                    });
                });
        },
        [deviceId, vin, currentComponentType],
    );

    const handleInstallationComplete = useCallback((notes: string) => {
        setCurrentNotes(notes);
        // Installation was successful, proceed to health check
        setComponentOnboardingState(ComponentOnboardingState.HEALTH_CHECK);
    }, []);

    const handleHealthCheckPassed = useCallback(
        (emeraldInfo?: any) => {
            // Store emerald information for display in success page
            if (emeraldInfo) {
                setEmeraldInformation(emeraldInfo);
            }

            // Health check passed, proceed with successful completion
            const updateRequest: UpdateDeviceComponentStatusRequest = {
                deviceId: deviceId,
                componentId: currentSerialNumber,
                vin: vin,
                componentType: currentComponentType!.value,
                status: OnboardingStatus.COMPLETED,
                notes: currentNotes,
            };

            updateDeviceComponentStatusHandler(updateRequest, (apiSuccess: boolean, result: any) => {
                if (!apiSuccess) {
                    logComponentInstallationEvent({
                        deviceId,
                        vin,
                        componentType: currentComponentType!.value,
                        componentId: currentSerialNumber,
                        eventType: EventType.FAILURE,
                        startTime,
                        error: 'Failed to update final component status',
                        apiError: result,
                    });
                }
            });

            // Clear existing component info so that we handle the installation completion as if it was a new install
            setExistingComponentInfo(null);

            // Mark the installation as successfully completed
            markDone(true, currentComponentType!, currentSerialNumber, currentNotes, deviceInfo);
        },
        [currentComponentType, currentSerialNumber, currentNotes, deviceInfo, markDone],
    );

    const handleFailureReported = useCallback(() => {
        markDone(
            false,
            currentComponentType!,
            currentSerialNumber,
            currentNotes,
            deviceInfo,
            'Failure reported during health check',
        );
        navigate('/');
    }, [markDone, currentComponentType, currentSerialNumber, currentNotes, deviceInfo, navigate]);

    return (
        <div>
            <Container>
                <SpaceBetween size="l">
                    <div
                        style={{
                            fontWeight: 'bold',
                            fontSize: '2em',
                        }}
                    >
                        Install Device Component
                    </div>

                    {showFailedInstallationWarning && (
                        <Alert type="warning" header="Existing Installation Failed">
                            There is an existing {currentComponentType?.label || 'component'} installation that failed.
                            Loading the health check to verify the current component status.
                        </Alert>
                    )}

                    {componentOnboardingState === ComponentOnboardingState.COMPONENT_INSTALL_PRECHECK && (
                        <ComponentCheck
                            deviceId={deviceId}
                            vin={vin}
                            onCheckComplete={handlePreCheckComplete}
                            onCancel={handleCancel}
                            disabled={showFailedInstallationWarning}
                        />
                    )}

                    {componentOnboardingState === ComponentOnboardingState.SOFTWARE_PLATFORM_CHECK &&
                        currentComponentType && (
                            <SoftwarePlatformCheck
                                deviceId={deviceId}
                                vin={vin}
                                onSuccess={handleSoftwarePlatformCheckSuccess}
                                onReturnHome={handleCancel}
                                componentType={currentComponentType}
                            />
                        )}

                    {componentOnboardingState === ComponentOnboardingState.COMPONENT_INSTALL &&
                        currentComponentType && (
                            <ComponentInstall
                                deviceId={deviceId}
                                vin={vin}
                                componentType={currentComponentType}
                                onInstallComponent={handleInstallComponentFromNew}
                                onCancel={handleCancel}
                                isSubmitting={isSubmittingInstall}
                            />
                        )}

                    {componentOnboardingState === ComponentOnboardingState.IN_PROGRESS && currentComponentType && (
                        <InstallationProgress
                            deviceId={deviceId}
                            vin={vin}
                            componentType={currentComponentType}
                            serialNumber={currentSerialNumber}
                            notes={currentNotes}
                            onInstallationComplete={handleInstallationComplete}
                            onCancel={handleCancel}
                            existingComponentInfo={existingComponentInfo}
                        />
                    )}

                    {componentOnboardingState === ComponentOnboardingState.HEALTH_CHECK && currentComponentType && (
                        <EmeraldHealthChecker
                            deviceId={deviceId}
                            vin={vin}
                            onHealthCheckPassed={handleHealthCheckPassed}
                            onReportFailure={handleFailureReported}
                            componentType={currentComponentType}
                            serialNumber={currentSerialNumber}
                        />
                    )}

                    {componentOnboardingState === ComponentOnboardingState.FINISHED_SUCCESS && currentComponentType && (
                        <InstallationSuccess
                            deviceId={deviceId}
                            vin={vin}
                            componentType={currentComponentType}
                            serialNumber={currentSerialNumber}
                            notes={currentNotes}
                            existingComponentInfo={existingComponentInfo}
                            emeraldInformation={emeraldInformation}
                            onGoHome={handleCancel}
                        />
                    )}
                </SpaceBetween>
            </Container>
        </div>
    );
};

export default ComponentOnboardingFormPage;
